import { z } from 'zod'

import { moneySchema, percentSchema } from '@/schemas/common'

/**
 * The market heatmap: thirteen sector tiles, or the names inside one sector.
 *
 * Two halves, in the order the bytes travel: what mm-be sends for each level
 * of `GET /api/v5/market/heatmap/`, then the one shape the screen draws from.
 */

// ── The wire ────────────────────────────────────────────────────────────────

/**
 * One SECTOR — a row of `/market/heatmap/` with no `?sector=`.
 *
 * The spec names this shape (`SectorHeatmapTile`) and `heatmapService.test.ts`
 * holds the two to the same input type, so a regenerated spec that moves a
 * field breaks the build. It is parsed rather than trusted anyway: the old
 * contract returned NAMES at this level, and a service that read those typed
 * fields off sector rows drew the whole market as one "Other" tile without a
 * single error. A parse turns the next contract change into one.
 *
 * `size` is the chosen sizing summed across the sector; `change_percent` is
 * the sector's market-cap-weighted move, the same whatever the sizing;
 * `name_count` and `market_cap_share` exist for the drill-in's caption. Every
 * null is mm-be's "not recorded" (its rule 5), never a zero.
 */
export const sectorHeatmapTileSchema = z.object({
  key: z.string().min(1),
  name: z.string().min(1),
  size: moneySchema.nullable(),
  change_percent: percentSchema.nullable(),
  name_count: z.number().int().nonnegative(),
  market_cap_share: percentSchema.nullable(),
})

/**
 * One NAME inside a sector — a row of `/market/heatmap/?sector=<key>`.
 *
 * The spec has no schema for it: mm-be declares the view's response once, as
 * `SectorHeatmapTile[]`, and describes this level only in prose. So this is
 * the only definition of the shape in the app, mirrored off mm-be's
 * `HeatmapTileSerializer` (`api/v5/market/serializers.py`, origin/dev
 * `bb2a6a8`): `size` may be null (no listed shares for a cap, no trade for a
 * turnover), `ltp` and `change_percent` may not.
 */
export const stockHeatmapTileSchema = z.object({
  key: z.string().min(1),
  name: z.string().min(1),
  sector_key: z.string().min(1).nullable(),
  sector_name: z.string().min(1).nullable(),
  size: moneySchema.nullable(),
  ltp: moneySchema,
  change_percent: percentSchema,
})

// ── What the screen draws ───────────────────────────────────────────────────

/**
 * What tile area stands for: the three chips above the map. The URL's words —
 * `heatmapService` maps them onto mm-be's `sizing` values.
 */
export const heatSizingSchema = z.enum(['cap', 'turnover', 'volume'])

/**
 * A tile is a tile whether it stands for a group or a company, so the drill-in
 * is a query parameter rather than a second shape — and `id` is the kebab
 * sector id at the top level and a symbol underneath, which is exactly what
 * the tile's link needs.
 *
 * Both numbers are plain numbers, not `Money`: neither is printed as a figure
 * a member reads off. `percentChange` picks one of five colour bands and
 * `size` only matters as a ratio to its neighbours — the same exemption the
 * chart wrappers have. Every rupee on these screens is still `Money`.
 */
export const heatTileSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  /** Today's move. Null when mm-be recorded none — never painted as Flat. */
  percentChange: z.number().nullable(),
  /**
   * mm-be's `size` for the chosen sizing. Null when that figure is not on
   * record for the tile, which is not the same claim as zero.
   */
  size: z.number().nonnegative().nullable(),
  /**
   * A sector's count of names and its share of the whole market's cap, in
   * percent: the two figures its drill-in caption prints, and its popover
   * repeats. Sector tiles only — a name carries neither — and the share is
   * absent where mm-be has none on record.
   */
  nameCount: z.number().int().nonnegative().optional(),
  marketCapShare: z.number().nonnegative().optional(),
})

export const heatmapQuerySchema = z.object({
  /** Absent at the top level; a kebab sector id inside a drill-in. */
  sector: z.string().min(1).optional(),
  sizing: heatSizingSchema,
})

export const heatmapSchema = z.object({
  /**
   * May be empty: mm-be answers a sector key it does not know with a 200 and
   * no rows, which is a wrong link, not a failed request.
   */
  tiles: z.array(heatTileSchema),
  /**
   * The line beside the breadcrumb: "Tap a sector to open its names" at the
   * top, "19 names · 29% of market cap · −1.22% today" inside one. It reads as
   * a caption in both places, so it is one field rather than a mode flag the
   * screen would have to branch on.
   */
  caption: z.string().min(1),
  /**
   * The breadcrumb's last step inside a drill-in — mm-be's own sector name,
   * e.g. "Commercial Banks". Absent at the top level, and absent in a drill-in
   * mm-be could not name, where the crumb falls back to the id.
   */
  sectorName: z.string().min(1).optional(),
  /** The provenance line under the legend, including what sized the tiles. */
  source: z.string().min(1),
})

export type SectorHeatmapTile = z.infer<typeof sectorHeatmapTileSchema>
export type StockHeatmapTile = z.infer<typeof stockHeatmapTileSchema>
export type HeatSizing = z.infer<typeof heatSizingSchema>
export type HeatTile = z.infer<typeof heatTileSchema>
export type HeatmapQuery = z.infer<typeof heatmapQuerySchema>
export type Heatmap = z.infer<typeof heatmapSchema>
